import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";
import { UNIVERSITY_ID } from "@/constants/admin/sectionTypes";

export interface IAuthor {
  id: number;
  university_id: number;
  name: string;
  /** Path of the page that opens this author's profile, e.g. "/blog/authors/rahul-singh". */
  slug: string | null;
  tagline: string | null;
  description: string;
  linkedin_url: string | null;
  quora_url: string | null;
  twitter_url: string | null;
  author_blog_url: string | null;
  profile_image_url: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  associated_date: string | null;
  domain: string | null;
  current_role: string | null;
  educational_qualification: string | null;
  expertise: string | null;
  tools_technologies: string | null;
  certifications: string | null;
  is_top_contributor: boolean;
  is_author_of_month: boolean;
  custom_badge_enabled: boolean;
  custom_badge_label: string | null;
  custom_badge_icon_url: string | null;
  is_active: boolean;
}

export type IAuthorPayload = Partial<Omit<IAuthor, "id">> & {
  university_id: number;
};

/** Blog page linked to an author — shown in the unpublish confirmation modal. */
export interface IAuthorLinkedPage {
  id: number;
  name: string;
  slug_1: string | null;
  slug_2: string | null;
  slug_3: string | null;
  number_of_slugs: number;
  is_active: boolean;
}

export async function listAuthors(universityId: number) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.AUTHOR.LIST, {
      params: { university_id: universityId },
    });
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

export async function getAuthorById(id: number, universityId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.AUTHOR.GET_BY_ID(id),
      { params: { university_id: universityId } },
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

/** Blog pages currently pointing at this author (drives the unpublish warning modal). */
export async function getAuthorLinkedPages(id: number, universityId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.AUTHOR.LINKED_PAGES(id),
      { params: { university_id: universityId } },
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

export async function createAuthor(payload: IAuthorPayload) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.AUTHOR.CREATE,
      payload,
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

export async function updateAuthor(id: number, payload: IAuthorPayload) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.AUTHOR.UPDATE(id),
      payload,
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

export async function deleteAuthor(id: number, universityId: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.AUTHOR.DELETE(id),
      { params: { university_id: universityId } },
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
}

/**
 * Published authors formatted for the Author Profile widget's dropdown.
 * Shape matches the `dynamicOptionsConfig` contract ({ data: [...] }).
 */
export async function getActiveAuthorsForWidget() {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.AUTHOR.LIST, {
      params: { university_id: UNIVERSITY_ID },
    });
    if (response?.status !== 200) return { data: [] };
    const rows: IAuthor[] = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
    return { data: rows.filter((row) => row.is_active) };
  } catch (error) {
    handleApiError(error as AxiosError);
    return { data: [] };
  }
}
